import type { ComputedRef, MaybeRefOrGetter, Ref, ShallowRef } from 'vue';
import type { CompositePrimaryKey, FieldSelection } from '#layers/data-model/public';
import { pickNonSpecialProps } from '@rstore/shared';
import { computed, ref, shallowRef, toValue, watch } from 'vue';
import { createRequestTracker } from '#layers/base/public';

/**
 * RStore collection API type with form methods.
 * Uses a minimal interface since the full type is complex and internal to rstore.
 */
export interface RStoreCollectionApi {
	createForm: (options?: { defaultValues?: () => any; resetOnSuccess?: boolean }) => any;
	updateForm: (key: any) => Promise<any>;
	findFirst: (options: any) => Promise<any>;
	peekFirst: (key: any) => any;
	getKey: (item: any) => string | number | null | undefined;
}

export interface UseRStoreFormObjectOptions<T> {
	/** Default values for create mode */
	defaultValues?: () => Partial<T>;

	/** Fields required to initialize and refresh the form item */
	selection?: MaybeRefOrGetter<FieldSelection | undefined>;

	/** Reset form after successful create */
	resetOnSuccess?: MaybeRefOrGetter<boolean | undefined>;

	/** Initial values to apply on top of loaded data */
	initialEdits?: MaybeRefOrGetter<Partial<T> | undefined>;

	/** Custom error handler. Return true to suppress default behavior */
	onError?: (error: Error) => boolean | undefined;
}

export interface UseRStoreFormObjectReturn {
	/** Reactive form object with $submit, $hasChanges, $reset, $schema, $loading, $error */
	formObject: ShallowRef<any>;

	/** True when itemKey is null/undefined */
	isCreate: ComputedRef<boolean>;

	/** True when form is loading */
	isLoading: Ref<boolean>;

	/** Error during form initialization */
	initError: Ref<Error | null>;

	/** Whether form has unsaved changes */
	hasChanges: ComputedRef<boolean>;
}

export function useRStoreFormObject<T>(
	collection: MaybeRefOrGetter<RStoreCollectionApi | undefined>,
	itemKey: MaybeRefOrGetter<CompositePrimaryKey | null | undefined>,
	options?: UseRStoreFormObjectOptions<T>,
): UseRStoreFormObjectReturn {
	const formObject = shallowRef<any>(null);
	const isLoading = ref(false);
	const initError = ref<Error | null>(null);

	const initializationRequests = createRequestTracker();

	// Extract a stable key string for comparison, preventing re-init when
	// the itemKey object reference changes but the actual primary key stays the same
	const stableItemKey = computed(() => {
		const coll = toValue(collection);
		const key = toValue(itemKey);
		if (!coll || key == null) return null;
		const resolved = coll.getKey(key);
		if (resolved == null) return; // invalid key
		return resolved;
	});

	// In create mode `selection` is not used by `createForm`. Watching it there
	// would refire the watcher on any downstream field-selection change (e.g.
	// when a relation field lazily resolves its opposite collection), recreating
	// the form object and wiping anything the user has typed. Mask `selection` to
	// `undefined` while in create mode so the watcher only refires for inputs
	// that actually affect form creation.
	const watchedSelection = computed(() =>
		stableItemKey.value === null ? undefined : toValue(options?.selection),
	);

	watch(
		[() => toValue(collection), stableItemKey, watchedSelection],
		async ([coll, storeKey, selection]) => {
			const request = initializationRequests.start();
			initError.value = null;

			try {
				if (!coll) {
					formObject.value = null;
					return;
				}

				if (storeKey === undefined) {
					throw new Error(`Invalid item key: ${JSON.stringify(toValue(itemKey))}`);
				}

				const isCreateMode = storeKey === null;

				if (isCreateMode) {
					// Merge initial edits into default values so they are part of the
					// baseline and don't trigger $hasChanges() on a fresh form.
					const edits = toValue(options?.initialEdits);
					const baseDefaultValues = options?.defaultValues;

					const defaultValues = edits
						? () => ({ ...baseDefaultValues?.(), ...pickNonSpecialProps(edits) })
						: baseDefaultValues;

					// Sync operation
					formObject.value = coll.createForm({
						defaultValues,
						resetOnSuccess: toValue(options?.resetOnSuccess),
					});
				}
				else {
					// Async operation - need loading state
					isLoading.value = true;

					const cached = coll.peekFirst(storeKey);
					let loadOptions: string | number | ({ key: string | number; fetchPolicy?: 'cache-only' } & FieldSelection);

					if (selection === undefined) {
						loadOptions = cached
							? { key: storeKey, fetchPolicy: 'cache-only' }
							: storeKey;
					}
					else if (cached) {
						loadOptions = {
							key: storeKey,
							fetchPolicy: 'cache-only',
							...selection,
						};
					}
					else {
						loadOptions = {
							key: storeKey,
							...selection,
						};
					}

					const form = await coll.updateForm(loadOptions);

					// Check if still relevant
					if (!request.isCurrent())
						return;

					formObject.value = form;

					// Apply initial edits after version check in update mode
					const edits = toValue(options?.initialEdits);

					if (edits) {
						Object.assign(formObject.value, pickNonSpecialProps(edits));
					}

					// Background refetch to replace potentially partial cached data
					if (cached) {
						const refreshOptions = selection === undefined
							? { key: storeKey, fetchPolicy: 'fetch-only' as const }
							: { key: storeKey, fetchPolicy: 'fetch-only' as const, ...selection };

						coll.findFirst(refreshOptions).then(() => {
							if (!request.isCurrent()) return;
							if (!formObject.value?.$hasChanges?.()) formObject.value?.$reset();
						}).catch(() => {
							// Silent — background optimization, form already loaded from cache
						});
					}
				}
			}
			catch (error) {
				if (!request.isCurrent())
					return;

				formObject.value = null;
				initError.value = error as Error;

				if (options?.onError?.(error as Error)) {
					initError.value = null; // Suppressed
				}
			}
			finally {
				if (request.isCurrent()) {
					isLoading.value = false;
				}
			}
		},
		{ immediate: true },
	);

	const isCreate = computed(() => toValue(itemKey) == null);
	const hasChanges = computed(() => formObject.value?.$hasChanges?.() ?? false);

	return { formObject, isCreate, isLoading, initError, hasChanges };
}
